'use client';
import { Modal as BzModal, ModalContent } from '@benzinga/core-ui';
import { ProUserSettingsManager } from '@benzinga/pro-user-settings';
import { SessionContext } from '@benzinga/session-context';
import React from 'react';
import styled from 'styled-components';
import { isNilOrEmpty, isNotNil } from '@benzinga/utils';
import { useUser } from '@benzinga/user-context';
import styles from './onboarding.module.scss';
import TextArea from 'antd/es/input/TextArea';
import { LoggingManager } from '@benzinga/session';
import { SubscriptionStatus } from '@benzinga/subscription-manager';

const MAX_NO_OF_DISMISS = 3;
const PRODUCT_SLUG = 'benzinga-pro';
export const ProOnboardingFeedbackModal = () => {
  const session = React.useContext(SessionContext);
  const user = useUser();

  const hasProductSlug = React.useMemo(
    () =>
      user?.subscriptions?.some(sub => sub.product === PRODUCT_SLUG && sub.status === SubscriptionStatus.trialing) ??
      false,
    [user?.subscriptions],
  );

  const proUserSettings = session.getManager(ProUserSettingsManager);
  const settings = proUserSettings.getSettings();
  const [showModal, setShowModal] = React.useState<boolean | undefined>(false);
  const [userInput, setUserInput] = React.useState('');

  const updateOnBoardingDismissCount = React.useCallback(
    (count: number) => {
      const setSetting = proUserSettings.setSetting;
      setSetting('onBoardingModalDismissCount', count);
    },
    [proUserSettings.setSetting],
  );

  const handleModalClose = React.useCallback(() => {
    setShowModal(undefined);
    updateOnBoardingDismissCount(Math.min(settings.onBoardingModalDismissCount + 1, MAX_NO_OF_DISMISS));
  }, [settings.onBoardingModalDismissCount, updateOnBoardingDismissCount]);

  const handleFeedbackSubmit = React.useCallback(async () => {
    try {
      const body = JSON.stringify({
        email: user?.email ?? '',
        userInput: userInput,
      });

      const response = await fetch('https://www.benzinga.com/api-next/pro-onboarding-form', {
        body,
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
      });

      if (!response || response.status !== 200) {
        const message: string =
          response && 'message' in response
            ? String(response?.message)
            : 'Error in submitting feedback! Please refresh and try again.';
        throw new Error(message);
      }

      setShowModal(undefined);
      updateOnBoardingDismissCount(MAX_NO_OF_DISMISS + 1);

      session.getManager(LoggingManager).log(
        'log',
        {
          category: 'Onboarding',
          message: "Thank you for your feedback! We appreciate your input & we'll be in touch soon!",
        },
        ['toast'],
      );
      return;
    } catch (error) {
      setShowModal(undefined);
      const message = error instanceof Error ? error.message : String(error);
      session.getManager(LoggingManager).log(
        'error',
        {
          category: 'Onboarding',
          message: message ?? 'Failed to submit feedback',
        },
        ['toast'],
      );
    }
  }, [session, updateOnBoardingDismissCount, user?.email, userInput]);

  React.useEffect(() => {
    if (showModal === undefined) return;
    if (isNotNil(settings?.onBoardingModalDismissCount)) {
      setShowModal((settings?.onBoardingModalDismissCount ?? 0) < MAX_NO_OF_DISMISS && hasProductSlug);
    }
  }, [hasProductSlug, settings?.onBoardingModalDismissCount, showModal, updateOnBoardingDismissCount]);
  return (
    <BzModal
      disableFullScreenButton
      doNotCloseOnBackgroundClick
      onClose={handleModalClose}
      size={{
        alert: true,
      }}
      title={'Quick Question'}
      visible={!!showModal}
    >
      <ModalContent className={styles.content}>
        <span>What Problem do you want Pro to solve for you?</span>
        <TextArea
          cols={4}
          onChange={e => setUserInput(e.target.value)}
          placeholder="Type your answer here..."
          rows={8}
          value={userInput}
        ></TextArea>
      </ModalContent>
      <ModalFooter className={styles.footer}>
        <button onClick={handleModalClose}>Maybe later</button>
        <button disabled={isNilOrEmpty(userInput)} onClick={handleFeedbackSubmit}>
          Share your thoughts
        </button>
      </ModalFooter>
    </BzModal>
  );
};

const ModalFooter = styled.footer`
  button:first-child {
    background: ${props => props.theme.colors.backgroundInactive};
    color: ${props => props.theme.colors.foregroundInactive};
  }

  button:last-child {
    color: ${props => props.theme.colors.buttonPrimary};
    background: ${props => props.theme.colors.brand};
  }
`;
